import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { ListPager } from "@/components/ui/list-pager";
import { RefreshCw, Search, Download, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

type AuditEntry = {
  id: string;
  created_at: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_role: string | null;
  action: string;
  category: string;
  outcome: "SUCCESS" | "FAILURE" | "DENIED";
  reason: string | null;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  changes: Record<string, { from?: any; to?: any }> | null;
  metadata: Record<string, any> | null;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
};

type Facets = {
  actions: { action: string; category: string; count: number }[];
  actors: { actor_id: string; name: string | null; email: string | null; count: number }[];
};

type ChainReport = { ok: boolean; checked: number; broken_at?: string; problem?: string };

const PAGE_SIZE = 50;

const CATEGORIES: Record<string, string> = {
  auth: "Sign-in & passwords",
  user: "Users",
  role: "Roles & permissions",
  record: "Records",
  quote: "Quotes",
  settings: "Settings",
  data: "Data access",
  security: "Security alerts",
  audit: "Audit log",
};

/** Plain-English names for the recorded actions (anything else is shown as recorded) */
const ACTION_LABELS: Record<string, string> = {
  "auth.login.success": "Signed in",
  "auth.login.failure": "Sign-in failed",
  "auth.logout": "Signed out",
  "auth.account.locked": "Account locked",
  "auth.password.changed": "Changed own password",
  "auth.password.reset_requested": "Asked for a password reset",
  "auth.password.reset": "Reset password with a link",
  "auth.password.recovered": "Password recovered from server console",
  "user.created": "Created user",
  "user.updated": "Edited user",
  "user.role.changed": "Changed role / manager",
  "user.suspended": "Suspended user",
  "user.reactivated": "Reactivated user",
  "user.deleted": "Deleted user",
  "user.unlocked": "Unlocked sign-in",
  "user.invite.sent": "Sent invite",
  "user.invite.accepted": "Accepted invite",
  "user.password_reset.sent": "Sent password reset link",
  "role.created": "Created role",
  "role.updated": "Edited role",
  "role.deleted": "Deleted role",
  "role.permissions.changed": "Changed role permissions",
  "lead.reassigned": "Reassigned lead",
  "lead.claimed": "Claimed lead",
  "lead.deleted": "Deleted lead",
  "deal.deleted": "Deleted deal",
  "account.deleted": "Deleted account",
  "contact.deleted": "Deleted contact",
  "product.deleted": "Deleted product",
  "product.deactivated": "Deactivated product",
  "deal_history.deleted": "Deleted deal history entry",
  "quote.approved": "Approved quote",
  "quote.rejected": "Rejected quote",
  "quote.sent": "Sent quote",
  "settings.company.updated": "Changed company details",
  "settings.rule.created": "Added business rule",
  "settings.rule.updated": "Changed business rule",
  "settings.rule.deleted": "Deleted business rule",
  "settings.whatsapp.updated": "Changed WhatsApp connection",
  "mailbox.connected": "Connected mailbox",
  "mailbox.disconnected": "Disconnected mailbox",
  "report.exported": "Exported report",
  "file.downloaded": "Downloaded deal file",
  "security.alert": "Security alert raised",
  "audit.viewed": "Viewed audit log",
  "audit.exported": "Exported audit log",
  "audit.verified": "Checked audit log integrity",
  "audit.retention.purged": "Archived old log entries",
};

const actionLabel = (a: string) => ACTION_LABELS[a] || a;

const selectClass =
  "h-9 w-full rounded-md border border-border bg-background px-3 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

function OutcomeBadge({ outcome }: { outcome: AuditEntry["outcome"] }) {
  if (outcome === "SUCCESS") return <Badge variant="success" className="text-[11px]">Success</Badge>;
  if (outcome === "DENIED") return <Badge variant="warning" className="text-[11px]">Denied</Badge>;
  return <Badge variant="destructive" className="text-[11px]">Failed</Badge>;
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  SALES_HEAD: "Sales Head",
  SALES_EXEC: "Sales Executive",
};
const roleLabel = (r?: string | null) => (r ? ROLE_LABELS[r.toUpperCase()] || r.replace(/_/g, " ").toLowerCase() : "");

const TARGET_LABELS: Record<string, string> = {
  user: "User",
  role: "Role",
  lead: "Lead",
  deal: "Deal",
  account: "Account",
  contact: "Contact",
  product: "Product",
  quotation: "Quote",
  report: "Report",
  deal_file: "Deal file",
  deal_changelog: "Deal history entry",
  setting_rule: "Business rule",
  company: "Company",
  mailbox: "Mailbox",
  whatsapp_account: "WhatsApp number",
};
const targetLabel = (t?: string | null) => (t ? TARGET_LABELS[t] || t.replace(/_/g, " ") : "");

/** Readable names for changed fields and detail keys */
const FIELD_LABELS: Record<string, string> = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  username: "Username",
  mobile: "Mobile",
  role: "Role",
  manager: "Reports to",
  status: "Status",
  owner: "Owner",
  value: "Value",
  name: "Name",
  code: "Code",
  discount_limit_pct: "Discount limit (%)",
  permissions_granted: "Permissions added",
  permissions_removed: "Permissions removed",
  rows: "Rows exported",
  filename: "File",
  identifier: "Email or username typed",
  emailed: "Email sent",
  expires_at: "Link expires",
  resend: "Resent",
  sessions_ended: "Signed out everywhere",
  other_sessions_ended: "Other devices signed out",
  locked_minutes: "Locked for (minutes)",
  deal_id: "Deal",
  to: "Sent to",
  cc: "Copy to",
  via: "Sent through",
  grand_total: "Grand total",
  max_discount_pct: "Highest discount (%)",
  approval_reasons: "Why it needed approval",
  owner_id: "Owner",
  removed: "Entries removed",
  older_than: "Older than",
  retention_months: "Keep for (months)",
  archive_file: "Archive file",
  checked: "Entries checked",
  failed_last_10_min: "Failed sign-ins (10 min)",
  notified: "Super Admins notified",
  mailbox: "Mailbox",
  reconnected: "Reconnected",
  credentials_replaced: "Credentials replaced",
  filters: "Filters used",
  head_id: "Sales Head filter",
  user_id: "User filter",
  from: "From",
  days: "Days",
  smtp_host: "Sending server",
  smtp_port: "Sending port",
  imap_host: "Receiving server",
  imap_port: "Receiving port",
  display_name: "Display name",
};
const fieldLabel = (k: string) => {
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  if (k.startsWith("data_scope.")) return `${k.slice(11).replace(/_/g, " ")} visibility`.replace(/^./, (c) => c.toUpperCase());
  return k.replace(/[._]/g, " ").replace(/^./, (c) => c.toUpperCase());
};

const SCOPE_LABELS: Record<string, string> = { own: "Own records", team: "Team records", all: "All records" };
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** A value as a person would say it: role names, Yes/No, dates in local time, lists joined */
function friendly(v: any, key = ""): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.map((x) => friendly(x)).join(", ") : "—";
  if (typeof v === "object") {
    const parts = Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== "");
    return parts.length ? parts.map(([k, x]) => `${fieldLabel(k)}: ${friendly(x, k)}`).join(" · ") : "—";
  }
  const s = String(v);
  if (key === "role" || ROLE_LABELS[s]) return roleLabel(s);
  if (key.startsWith("data_scope.") && SCOPE_LABELS[s]) return SCOPE_LABELS[s];
  if (key === "status") return s === "ACTIVE" ? "Active" : s === "SUSPENDED" ? "Suspended" : s;
  if (ISO_DATE.test(s)) return new Date(s).toLocaleString();
  return s;
}

/** "Chrome on Linux" from a browser string; anything unrecognised is shown as it is */
function deviceOf(ua?: string | null): string {
  if (!ua) return "Unknown";
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : null;
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iPhone / iPad" : /Mac OS X/.test(ua) ? "Mac" : /Linux/.test(ua) ? "Linux" : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || (ua.length > 60 ? `${ua.slice(0, 60)}…` : ua);
}

function timeAgo(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

/** One plain sentence: "Rohan Kapoor exported the report “pipeline”." */
function summary(e: AuditEntry): string {
  const who = e.actor_id ? e.actor_name || e.actor_email || "Someone" : e.category === "auth" ? "Someone who was not signed in" : "The system";
  const what = actionLabel(e.action).replace(/^./, (c) => c.toLowerCase());
  const record = e.target_label ? ` — ${targetLabel(e.target_type).toLowerCase() || "record"} “${e.target_label}”` : "";
  const result = e.outcome === "SUCCESS" ? "" : e.outcome === "DENIED" ? " (blocked)" : " (failed)";
  return `${who}: ${what}${record}${result}.`;
}

/**
 * Audit log (Super Admins): who did what, to which record, when, from where, and the result.
 * Entries cannot be edited or deleted, and "Check integrity" proves none were.
 */
export function AuditLogPage() {
  const { error, success } = useToast();
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState<Facets>({ actions: [], actors: [] });
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [chain, setChain] = useState<ChainReport | null>(null);
  const [checking, setChecking] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [outcome, setOutcome] = useState("");
  const [actorId, setActorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    if (debouncedSearch) qs.set("search", debouncedSearch);
    if (category) qs.set("category", category);
    if (action) qs.set("action", action);
    if (outcome) qs.set("outcome", outcome);
    if (actorId) qs.set("actor_id", actorId);
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    return qs;
  }, [debouncedSearch, category, action, outcome, actorId, from, to]);

  // Any filter change goes back to the first page
  useEffect(() => setPage(1), [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(query);
      qs.set("page", String(page));
      qs.set("page_size", String(PAGE_SIZE));
      const res: any = await api.get(`/api/AuditLog/List?${qs}`);
      setRows(res?.rows || []);
      setTotal(Number(res?.total || 0));
    } catch (err: any) {
      error("Could not load the audit log", err.message || "Please try again");
    } finally {
      setLoading(false);
    }
  }, [query, page, error]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/api/AuditLog/Facets").then((res: any) => setFacets(res || { actions: [], actors: [] })).catch(() => undefined);
  }, []);

  const actionsInCategory = facets.actions.filter((a) => !category || a.category === category);

  const verify = async () => {
    setChecking(true);
    try {
      const res: any = await api.post("/api/AuditLog/Verify");
      setChain(res);
      if (res?.ok) success("Audit log intact", `All ${res.checked} entries check out`);
      else error("Audit log tampering detected", `Broken at entry #${res?.broken_at}`);
      load();
    } catch (err: any) {
      error("Integrity check failed", err.message || "Please try again");
    } finally {
      setChecking(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res: any = await api.get(`/api/AuditLog/Export?${query}`);
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || "audit-log.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      error("Export failed", err.message || "Please try again");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setAction("");
    setOutcome("");
    setActorId("");
    setFrom("");
    setTo("");
  };
  const anyFilter = !!(search || category || action || outcome || actorId || from || to);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Audit Log</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Who signed in, changed users or permissions, deleted or exported data, and changed settings. Entries can't be
            edited or deleted.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-9 gap-1.5">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={verify} disabled={checking} className="h-9 gap-1.5">
            {checking ? <Loader2 className="size-3.5 animate-spin" /> : <ShieldCheck className="size-3.5" />}
            Check integrity
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={exporting} className="h-9 gap-1.5 shadow-sm">
            {exporting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
            Export CSV
          </Button>
        </div>
      </div>

      {chain && (
        <div
          className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${
            chain.ok
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
              : "border-red-500/40 bg-red-500/10 text-red-800 dark:text-red-300"
          }`}
        >
          {chain.ok ? <ShieldCheck className="size-4 shrink-0" /> : <ShieldAlert className="size-4 shrink-0" />}
          <span>
            {chain.ok
              ? `Integrity check passed: all ${chain.checked} entries are unaltered and none are missing.`
              : `Integrity check FAILED at entry #${chain.broken_at}: ${chain.problem}. Entries before it are intact (${chain.checked} checked).`}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search person, record, event, IP or exact record id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9 text-sm"
          />
        </div>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setAction("");
          }}
          className={selectClass}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} className={selectClass} aria-label="Event">
          <option value="">All events</option>
          {actionsInCategory.map((a) => (
            <option key={a.action} value={a.action}>
              {actionLabel(a.action)} ({a.count})
            </option>
          ))}
        </select>
        <select value={actorId} onChange={(e) => setActorId(e.target.value)} className={selectClass} aria-label="Person">
          <option value="">Anyone</option>
          {facets.actors.map((a) => (
            <option key={a.actor_id} value={a.actor_id}>
              {a.name || a.email} {a.name && a.email ? `(${a.email})` : ""}
            </option>
          ))}
        </select>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={selectClass} aria-label="Result">
          <option value="">Any result</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILURE">Failed</option>
          <option value="DENIED">Denied</option>
        </select>
        <div className="flex items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 text-xs" aria-label="From date" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-xs" aria-label="To date" />
        </div>
        {anyFilter && (
          <Button variant="ghost" size="sm" className="h-9 justify-self-start text-xs" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {loading && rows.length === 0 ? (
          <TableSkeleton rows={8} columns={6} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Time</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Record</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="whitespace-nowrap">IP address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">
                      {anyFilter ? "No entries match these filters." : "Nothing has been recorded yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelected(r)}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        <span className="block text-foreground">{new Date(r.created_at).toLocaleString()}</span>
                        <span>{timeAgo(r.created_at)}</span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.actor_id ? (
                          <>
                            <span className="font-medium text-foreground block">{r.actor_name || r.actor_email}</span>
                            <span className="text-muted-foreground">{roleLabel(r.actor_role)}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground italic">
                            {r.category === "auth" ? "Not signed in" : "System"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="font-medium text-foreground">{actionLabel(r.action)}</span>
                        {r.reason && <span className="block text-muted-foreground truncate max-w-xs">{r.reason}</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.target_label || r.target_id ? (
                          <>
                            <span className="text-foreground block truncate max-w-[220px]">{r.target_label || r.target_id}</span>
                            <span className="text-muted-foreground">{targetLabel(r.target_type)}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <OutcomeBadge outcome={r.outcome} />
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{r.ip || "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
        <ListPager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} disabled={loading} />
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl max-h-[88vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {actionLabel(selected.action)} <OutcomeBadge outcome={selected.outcome} />
                </DialogTitle>
                <DialogDescription className="text-sm text-foreground/80">{summary(selected)}</DialogDescription>
              </DialogHeader>

              {selected.reason && (
                <div
                  className={`rounded-md border p-2.5 text-xs sm:text-sm ${
                    selected.outcome === "SUCCESS"
                      ? "border-border bg-muted/40"
                      : "border-red-200 bg-red-50 text-red-800"
                  }`}
                >
                  <span className="font-semibold">{selected.outcome === "SUCCESS" ? "Note: " : "Reason: "}</span>
                  {selected.reason}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                <div className="rounded-md border border-border p-2.5">
                  <p className="text-muted-foreground mb-0.5">Who</p>
                  {selected.actor_id ? (
                    <>
                      <p className="font-medium text-foreground">{selected.actor_name || selected.actor_email}</p>
                      <p className="text-muted-foreground">
                        {[roleLabel(selected.actor_role), selected.actor_name ? selected.actor_email : null].filter(Boolean).join(" · ")}
                      </p>
                    </>
                  ) : (
                    <p className="text-foreground">{selected.category === "auth" ? "Not signed in" : "The system (automatic)"}</p>
                  )}
                </div>
                <div className="rounded-md border border-border p-2.5">
                  <p className="text-muted-foreground mb-0.5">When</p>
                  <p className="font-medium text-foreground">
                    {new Date(selected.created_at).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
                  </p>
                  <p className="text-muted-foreground">{timeAgo(selected.created_at)}</p>
                </div>
                <div className="rounded-md border border-border p-2.5">
                  <p className="text-muted-foreground mb-0.5">From</p>
                  <p className="font-medium text-foreground">{deviceOf(selected.user_agent)}</p>
                  <p className="text-muted-foreground">IP address {selected.ip || "unknown"}</p>
                </div>
                <div className="rounded-md border border-border p-2.5">
                  <p className="text-muted-foreground mb-0.5">{targetLabel(selected.target_type) || "Record"}</p>
                  <p className="font-medium text-foreground break-all">{selected.target_label || (selected.target_type ? "—" : "Not about a specific record")}</p>
                </div>
              </div>

              {selected.changes && Object.keys(selected.changes).length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1.5">What changed</p>
                  <div className="rounded-md border border-border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-2.5 py-1.5 font-medium w-1/4"></th>
                          <th className="text-left px-2.5 py-1.5 font-medium">Before</th>
                          <th className="text-left px-2.5 py-1.5 font-medium">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selected.changes).map(([field, c]) => (
                          <tr key={field} className="border-t border-border align-top">
                            <td className="px-2.5 py-1.5 font-medium">{fieldLabel(field)}</td>
                            <td className="px-2.5 py-1.5 text-red-700 dark:text-red-400 break-words">{friendly(c?.from, field)}</td>
                            <td className="px-2.5 py-1.5 text-emerald-700 dark:text-emerald-400 break-words">{friendly(c?.to, field)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1.5">More details</p>
                  <dl className="rounded-md border border-border divide-y divide-border text-xs">
                    {Object.entries(selected.metadata)
                      .filter(([, v]) => v !== null && v !== undefined && v !== "")
                      .map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[minmax(120px,40%)_1fr] gap-3 px-2.5 py-1.5">
                          <dt className="text-muted-foreground">{fieldLabel(k)}</dt>
                          <dd className="text-foreground break-words">{friendly(v, k)}</dd>
                        </div>
                      ))}
                  </dl>
                </div>
              )}

              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">Technical details</summary>
                <dl className="mt-2 grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 text-muted-foreground">
                  <dt>Entry</dt>
                  <dd className="font-mono">#{selected.id}</dd>
                  <dt>Event code</dt>
                  <dd className="font-mono">{selected.action}</dd>
                  <dt>Exact time (UTC)</dt>
                  <dd className="font-mono">{selected.created_at}</dd>
                  {selected.target_id && (
                    <>
                      <dt>Record id</dt>
                      <dd className="font-mono break-all">{selected.target_id}</dd>
                    </>
                  )}
                  <dt>Request id</dt>
                  <dd className="font-mono break-all">{selected.request_id || "—"}</dd>
                  <dt>Browser string</dt>
                  <dd className="break-all">{selected.user_agent || "—"}</dd>
                </dl>
              </details>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
